import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/query-client";
import { routeTree } from "./routeTree.gen";
import * as Sentry from "@sentry/react";
import LoadingScreen from "./routes/-loading";
import "./index.css";

const router = createRouter({
  routeTree,
  scrollRestoration: true,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: LoadingScreen,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://28b4f7ba698ac56ed0ea06b8ec7faa91@o4511689125724160.ingest.us.sentry.io/4511689251880960",
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
      <SidebarProvider>
        <TooltipProvider delayDuration={300}>
          <NuqsAdapter>
            <RouterProvider router={router} />
          </NuqsAdapter>
        </TooltipProvider>
      </SidebarProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

if ("serviceWorker" in navigator && "PushManager" in window) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      registration.update();
    })
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}
